import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, BellOff, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { 
  isPushSupported, 
  getNotificationPermission, 
  subscribeToPush, 
  unsubscribeFromPush,
  isSubscribed,
  registerServiceWorker
} from '../utils/pushNotifications';

const NotificationToggle = () => {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    const init = async () => {
      const isSupported = isPushSupported();
      setSupported(isSupported);
      
      if (isSupported) {
        setPermission(getNotificationPermission());
        await registerServiceWorker();
        const isSub = await isSubscribed();
        setSubscribed(isSub);
      }
    };
    init();
  }, []);

  const handleToggle = async () => {
    if (!supported) {
      toast.error('Push notifications are not supported in your browser');
      return;
    }

    setLoading(true);
    try {
      if (subscribed) {
        const result = await unsubscribeFromPush();
        if (result.success) {
          setSubscribed(false);
          toast.success('Notifications turned off');
        } else {
          toast.error(result.error || 'Failed to unsubscribe');
        }
      } else {
        const result = await subscribeToPush();
        if (result.success) {
          setSubscribed(true);
          toast.success('You\'ll be notified when the Sage has a new topic!');
        } else {
          if (result.error === 'Permission denied') {
            toast.error('Please allow notifications in your browser settings');
          } else {
            toast.error(result.error || 'Failed to subscribe');
          }
        }
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
      setPermission(getNotificationPermission());
    }
  };

  if (!supported) {
    return null;
  }

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleToggle}
      disabled={loading}
      className={`p-2 rounded-lg transition-colors relative ${
        subscribed 
          ? 'bg-vent-primary/20 text-vent-primary' 
          : 'hover:bg-white/10 text-zinc-400 hover:text-white'
      }`}
      data-testid="notification-toggle"
      title={subscribed ? 'Notifications On' : 'Enable Notifications'}
    >
      {loading && (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-5 h-5 border-2 border-current border-t-transparent rounded-full"
        />
      )}
      {!loading && subscribed && (
        <Bell className="w-5 h-5" fill="currentColor" />
      )}
      {!loading && !subscribed && (
        <BellOff className="w-5 h-5" />
      )}
      
      {subscribed && (
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-vent-for rounded-full" />
      )}
    </motion.button>
  );
};

export default NotificationToggle;
